// Step-up proof for a sign-off (Aaron 2026-10-09: "Password re-entry before a
// sign-off → Yes: step-up auth"; on for mustang, peak and g2; a 5-minute window;
// federated sign-ins refused). Design: repo/brain/projects/signoff-step-up-auth-2026-10-09.md.
//
// The session token cannot prove the signer is at the device NOW: both GCIP and
// Cognito keep the original auth_time through a refresh. So the signer re-enters
// their password, the IdP's OWN password sign-in mints a fresh ID token, and that
// token rides on the sign-off as X-Aeronet-Step-Up. The API checks it is the
// session's user and that auth_time is within its window (app/step_up.py).
//
//  * The password goes to the IdP only (GCIP accounts:signInWithPassword /
//    Cognito SRP), never to our API and never to storage.
//  * The fresh token is held in MEMORY only, and the stored session is left
//    alone: the GCIP refresh token is discarded, and the Cognito sign-in runs
//    against a throwaway storage so Amplify's session is not replaced.
//  * One proof covers every sign-off inside the window (a "Set as done" over
//    many tasks asks once).
//
// The API answers 428 when it wants a proof (missing or stale) and 403 when this
// proof can never do (another user, a federated sign-in). Both arrive in the
// shared axios error interceptor (src/api.ts), which calls handleStepUpError:
// a 428 opens the dialog and retries the request once with the proof.

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  ICognitoStorage,
} from "amazon-cognito-identity-js";

import { getAuthProvider, getCognitoConfig, getGcipConfig } from "../config/product";
import { getIdTokenJwt, getSessionUser } from "./session";

export const STEP_UP_HEADER = "X-Aeronet-Step-Up";
const DEFAULT_MAX_AGE_S = 300;
// Drop a held proof this long before the API would call it stale, so a slow
// request never carries a proof that expires in flight.
const MARGIN_S = 30;

/** The routes that finish a task, where a held proof is attached up front. */
const FINISHING_ROUTE = /\/v6\/production\/tasks\/(\d+\/sign-off|complete|status)$/;

interface HeldProof {
  token: string;
  usableUntil: number; // epoch ms
}

let held: HeldProof | null = null;

function claimsOf(token: string): Record<string, unknown> {
  try {
    const part = token.split(".")[1];
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

/** Keep a fresh proof for its window; the API, not this, decides it is valid. */
export function holdProof(token: string, maxAgeS: number = DEFAULT_MAX_AGE_S, now: number = Date.now()): void {
  const authTime = Number(claimsOf(token).auth_time);
  const from = Number.isFinite(authTime) && authTime > 0 ? authTime * 1000 : now;
  held = { token, usableUntil: from + (maxAgeS - MARGIN_S) * 1000 };
}

export function heldProof(now: number = Date.now()): string | null {
  if (held && now < held.usableUntil) return held.token;
  held = null;
  return null;
}

export function forgetProof(): void {
  held = null;
}

export function isFinishingRoute(url: string | undefined): boolean {
  return !!url && FINISHING_ROUTE.test(url.split("?")[0]);
}

/**
 * A Log Book entry's save, which signs when it sets a signature (Aircraft Patch 5, P2 d; the
 * API asks for a proof only then, behind aircraft_logbook_sign_step_up). Writes only — POST /
 * PUT of an entry, on the module's routes or the work order tab's — so the held proof never
 * rides on a read. Also an Authorised Request signature step's countersign (document execution,
 * version-36; app/authorised_requests/signatures/api.py), which always needs a proof.
 */
const SIGNING_ROUTE =
  /\/v6\/(aircraft_services(\/\d+)?|jobs\/\d+\/log-book-tab\/entries(\/\d+)?|authorised_request_signatures\/(steps|batches)\/\d+\/countersign)$/;

export function isSigningRoute(url: string | undefined, method: string | undefined): boolean {
  const m = String(method || "get").toLowerCase();
  return !!url && (m === "post" || m === "put") && SIGNING_ROUTE.test(url.split("?")[0]);
}

// --- the IdP's own password sign-in ----------------------------------------------

/** Who the dialog asks for: the session's user, read-only. */
export async function stepUpIdentity(): Promise<{ label: string; username: string } | null> {
  const user = await getSessionUser().catch(() => null);
  if (!user) return null;
  const email = (user.attributes?.email as string | undefined) || user.username || "";
  // Cognito signs in by its own username (cognito:username); GCIP by email.
  const username = getAuthProvider() === "gcip" ? email : user.username || email;
  return { label: email || username, username };
}

/** The IdP's refusals, in words a signer can act on. */
export class StepUpSignInError extends Error {}

function gcipMessage(code: string): string {
  if (code.startsWith("TOO_MANY_ATTEMPTS")) return "Too many attempts. Wait a few minutes, then try again.";
  if (code === "USER_DISABLED") return "This account is disabled.";
  return "Password incorrect.";
}

async function gcipProof(email: string, password: string): Promise<string> {
  const { apiKey } = getGcipConfig();
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // returnSecureToken is what returns an ID token; its refresh token is dropped.
    body: JSON.stringify({ email, password, returnSecureToken: true }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.idToken) {
    const code = String(data?.error?.message || "");
    if (!code.startsWith("TOO_MANY_ATTEMPTS") && (await sessionIsFederated())) {
      // Aaron 2026-10-09 Q3: federated signers are refused. Say so, rather than
      // "Password incorrect" to someone who never had an Aeronet password.
      throw new StepUpSignInError(
        "You signed in with Google. Sign-off needs an email and password sign-in: ask an administrator to set a password."
      );
    }
    throw new StepUpSignInError(gcipMessage(code));
  }
  return data.idToken as string;
}

/** Whether this GCIP session came from Google rather than a password (display only). */
async function sessionIsFederated(): Promise<boolean> {
  try {
    const provider = (claimsOf(await getIdTokenJwt()).firebase as { sign_in_provider?: string } | undefined)
      ?.sign_in_provider;
    return Boolean(provider) && provider !== "password";
  } catch {
    return false;
  }
}

/** A storage that forgets: the proof's Cognito session never touches Amplify's. */
function memoryStorage(): ICognitoStorage {
  const items: Record<string, string> = {};
  return {
    setItem: (k: string, v: string) => {
      items[k] = v;
    },
    getItem: (k: string) => items[k] ?? null,
    removeItem: (k: string) => {
      delete items[k];
    },
    clear: () => {
      Object.keys(items).forEach((k) => delete items[k]);
    },
  };
}

function cognitoProof(username: string, password: string): Promise<string> {
  const cfg = getCognitoConfig();
  if (!cfg.userPoolId || !cfg.userPoolWebClientId) {
    return Promise.reject(new StepUpSignInError("Sign-off is not set up for this site's sign-in."));
  }
  const Storage = memoryStorage();
  const pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.userPoolWebClientId, Storage });
  const user = new CognitoUser({ Username: username, Pool: pool, Storage });
  return new Promise((resolve, reject) => {
    // USER_SRP_AUTH (the default): the password never leaves the browser in clear.
    user.authenticateUser(new AuthenticationDetails({ Username: username, Password: password }), {
      onSuccess: (session) => resolve(session.getIdToken().getJwtToken()),
      onFailure: (err: any) => {
        const code = String(err?.code || err?.name || "");
        reject(
          new StepUpSignInError(
            code === "LimitExceededException" || code === "TooManyRequestsException"
              ? "Too many attempts. Wait a few minutes, then try again."
              : "Password incorrect."
          )
        );
      },
      newPasswordRequired: () => reject(new StepUpSignInError("This password must be changed before you can sign off.")),
      mfaRequired: () => reject(new StepUpSignInError("This account needs MFA, which sign-off does not support yet.")),
      totpRequired: () => reject(new StepUpSignInError("This account needs MFA, which sign-off does not support yet.")),
    });
  });
}

/** Re-run the IdP's password sign-in for the session's user; returns the fresh ID token. */
export function signInForProof(username: string, password: string): Promise<string> {
  return getAuthProvider() === "gcip" ? gcipProof(username, password) : cognitoProof(username, password);
}

// --- the dialog seam ---------------------------------------------------------------

/** Resolves with a fresh ID token, or null when the signer cancels. */
export type StepUpPrompt = (reason: string) => Promise<string | null>;

let prompt: StepUpPrompt | null = null;

/** StepUpHost registers itself here; the interceptor has no React of its own. */
export function registerStepUpPrompt(p: StepUpPrompt | null): void {
  prompt = p;
}

// --- the interceptor's half --------------------------------------------------------

export function stepUpCode(error: any): string | null {
  const status = error?.response?.status;
  const code = error?.response?.data?.detail?.code;
  return (status === 428 || status === 403) && typeof code === "string" && code.startsWith("step_up_") ? code : null;
}

/** Callers toast `detail`; a step-up refusal's detail is an object, so hand them its words. */
function withMessage(error: any, message: string): any {
  if (error?.response?.data) error.response.data = { ...error.response.data, detail: message };
  return error;
}

/**
 * The API asked for a proof (428) or refused one (403). On a 428 ask the signer,
 * then retry once with the proof through `retry`; on a 403 drop the held proof.
 * Rejects with a plain-string `detail` so every caller's existing toast reads.
 */
export async function handleStepUpError(error: any, retry: (cfg: any) => Promise<any>): Promise<any> {
  const code = stepUpCode(error);
  const detail = error?.response?.data?.detail ?? {};
  const message: string = detail.message || "Signing needs your password.";
  forgetProof();
  if (error?.response?.status === 403 || !prompt) {
    return Promise.reject(withMessage(error, message));
  }
  const cfg = error.config || {};
  if (cfg.__stepUpAsked) {
    // Asked once already for this request: do not loop on a clock that disagrees.
    return Promise.reject(withMessage(error, message));
  }
  const token = await prompt(code === "step_up_stale" ? "Your password check has expired." : "");
  if (!token) {
    return Promise.reject(withMessage(error, "Not signed: the password was not entered."));
  }
  holdProof(token, Number(detail.max_age_s) || DEFAULT_MAX_AGE_S);
  cfg.__stepUpAsked = true;
  if (typeof cfg.headers?.set === "function") cfg.headers.set(STEP_UP_HEADER, token);
  else cfg.headers = { ...(cfg.headers || {}), [STEP_UP_HEADER]: token };
  return retry(cfg);
}
